// PounceAgentDetail.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce agent ka detail page — dashboard ke Pounce card pe tap se khulta hai
// (/pounce-agent/:agentId). Receptionist wala /fast-agent-detail yahan kaam ka
// nahi: minutes, number assign, test call — Pounce me inme se kuch nahi hai.
//
// Saara data ek request se: GET /api/pounce/agents/:agentId/overview.
// Date filter (Today / Yesterday / 7 / 30 din) sirf counts badalta hai; graph
// hamesha pichhle 7 din ka hai (design me "Last 7 Days").
//
// Ye file sirf data laati hai aur sections ko jodti hai; har section ka apna
// component apne folder me hai (./PounceAgentHeader, ./PounceLiveActivity, …).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getPounceAgentOverview } from "../../Store/apiStore";
import styles from "./PounceAgentDetail.module.css";
import { rangeBounds } from "../../utils/pounceAgentHelpers";
import PounceAgentHeader from "./PounceAgentHeader/PounceAgentHeader";
import PounceAgentSkeleton from "./PounceAgentSkeleton/PounceAgentSkeleton";
import PounceAgentInfoCard from "./PounceAgentInfoCard/PounceAgentInfoCard";
import PounceAgentActions from "./PounceAgentActions/PounceAgentActions";
import PounceAddLead from "./PounceAddLead/PounceAddLead";
import pounceAddLeadStyles from "./PounceAddLead/PounceAddLead.module.css";
import PounceCallAnalytics from "./PounceCallAnalytics/PounceCallAnalytics";
import PounceQualifiedChart from "./PounceQualifiedChart/PounceQualifiedChart";
import PounceLiveActivity from "./PounceLiveActivity/PounceLiveActivity";
import PounceFooterNav from "./PounceFooterNav/PounceFooterNav";

const PounceAgentDetail = () => {
  const { agentId: paramAgentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const agentId =
    paramAgentId ||
    location.state?.agentId ||
    sessionStorage.getItem("SelectAgentId") ||
    localStorage.getItem("SelectAgentId") ||
    "";

  useEffect(() => {
    if (agentId) {
      sessionStorage.setItem("SelectAgentId", agentId);
    }
  }, [agentId]);

  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leadSuccessMsg, setLeadSuccessMsg] = useState("");

  // silent = background refresh (lead judne ke baad): skeleton nahi dikhta,
  // warna page ke sections unmount ho kar apna state kho dete.
  const load = useCallback(async (silent) => {
    if (!agentId) {
      setError("No Outbound Caller agent selected.");
      setLoading(false);
      return;
    }
    if (silent !== true) setLoading(true);
    setError("");
    try {
      const { from, to } = rangeBounds(range);
      const res = await getPounceAgentOverview(agentId, {
        from: from.toISOString(),
        to: to.toISOString(),
      });
      setData(res);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "This Outbound Caller agent wasn't found."
          : "We couldn't load this agent. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [agentId, range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!leadSuccessMsg) return undefined;
    const t = setTimeout(() => setLeadSuccessMsg(""), 5000);
    return () => clearTimeout(t);
  }, [leadSuccessMsg]);

  useEffect(() => {
    if (location.state?.pounceLeadAddedAt) {
      load(true);
      if (location.state.pounceLeadSuccess) {
        setLeadSuccessMsg(location.state.pounceLeadSuccess);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, load]);

  const agent = data?.agent;

  return (
    <div className={styles.page}>
      <PounceAgentHeader
        range={range}
        onRangeChange={setRange}
        onBack={() => navigate("/dashboard")}
      />

      {error && !data ? (
        <div className={styles.stateBox}>
          <p>{error}</p>
          <button type="button" className={styles.retryBtn} onClick={load}>
            Try again
          </button>
        </div>
      ) : loading || !agent ? (
        <PounceAgentSkeleton />
      ) : (
        <>
          {/* Card + actions .page ke 16px side padding se BAHAR nikalte hain
              (.cardBleed); neeche ka baaki sab padding ke andar hi rehta hai. */}
          <div className={styles.cardBleed}>
            <PounceAgentInfoCard agent={agent} agentId={agentId} />
            <PounceAgentActions />
          </div>

          {/* Agent chalu ho AUR owner ne Pounce off na kiya ho (header ka toggle). */}
          {leadSuccessMsg && (
            <p className={pounceAddLeadStyles.success} role="status">
              {leadSuccessMsg}
            </p>
          )}
          <PounceAddLead live={agent.live !== false && !agent.paused} onAdded={() => load(true)} />

          <PounceCallAnalytics counts={data?.counts} loading={loading} />
          <PounceQualifiedChart qualifiedAt={data?.qualifiedAt} />
          <PounceLiveActivity activity={data?.activity} timeZone={data?.agent?.displayTimezone} />
          <PounceFooterNav agentId={agentId} />
        </>
      )}
    </div>
  );
};

export default PounceAgentDetail;
